import { useAuthStore } from '@/stores/auth-store';

const LEGACY_SESSION_HEADER = 'x-trace-id';
const UI_SESSION_HEADER = 'x-ui-session-id';

interface UiSessionOptions {
   sessionInitialized?: boolean;
   signal?: AbortSignal | null;
}

/** Stop awaiting initialization when its request has already been cancelled. */
async function initializeForRequest(initialize: () => Promise<void>, signal?: AbortSignal | null): Promise<void> {
   if (!signal) {
      await initialize();
      return;
   }

   signal.throwIfAborted();
   let onAbort!: () => void;
   const aborted = new Promise<never>((_resolve, reject) => {
      onAbort = () => reject(signal.reason);
      signal.addEventListener('abort', onAbort, { once: true });
   });

   try {
      await Promise.race([initialize(), aborted]);
   }
   finally {
      signal.removeEventListener('abort', onAbort);
   }
}

/** Attach the current UI session ID without retaining a caller-supplied stale value. */
export async function attachUiSessionId(
   headers: Headers,
   { sessionInitialized = false, signal }: UiSessionOptions = {},
): Promise<void> {
   const authStore = useAuthStore();
   headers.delete(LEGACY_SESSION_HEADER);
   headers.delete(UI_SESSION_HEADER);

   if (!authStore.accessTokenObject.jti && !sessionInitialized) {
      await initializeForRequest(authStore.init, signal);
   }

   const sessionId = authStore.accessTokenObject.jti;
   if (sessionId) {
      headers.set(UI_SESSION_HEADER, sessionId);
   }
}
